import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import html2pdf from "html2pdf.js";
import { Download, ExternalLink, MapPin, ArrowLeft } from "lucide-react";

const CoverPage = () => (
  <header className="report-cover page-1">
    <div className="cover-accent"></div>
    <div className="cover-content">
      <h1 className="cover-title">
        Michigan Healthcare Fraud<br />
        Case Analysis
      </h1>
      <div className="cover-subtitle">
        How Serialized Reimbursement Verification<br />
        Prevents Each Scheme
      </div>
      <div className="cover-companion">
        Companion Analysis to the MENDRx State Policy Briefing
      </div>
      <div className="cover-state">
        <MapPin size={18} />
        <span>State of Michigan | March 2026</span>
      </div>
      <div className="cover-prepared">
        <span className="prepared-label">Prepared by</span>
        <span className="prepared-company">MENDRx</span>
      </div>
      <div className="cover-classification">CONFIDENTIAL</div>
    </div>
    <div className="page-number">1</div>
  </header>
);

/* PDF Page 1 */
const ExecSummaryAndCase1Header = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">EXECUTIVE SUMMARY</h2>
    <p className="body-text">
      This analysis examines every documented healthcare fraud case in Michigan from 2022–2026,
      with particular focus on pharmacy-specific fraud schemes. Of the 12 cases analyzed, representing
      $50.8 million in combined fraud, 8 cases ($49.3 million) involved pharmacy or
      pharmaceutical fraud directly addressable by serialized reimbursement verification.
    </p>
    <h3 className="subsection-title">Key Findings:</h3>
    <ul className="bullet-list">
      <li><strong>Total cases analyzed:</strong> 12</li>
      <li><strong>Pharmacy/pharmaceutical fraud:</strong> 8 cases with confirmed or estimated values totaling $51.2M+</li>
      <li><strong>Non-pharmacy fraud:</strong> 4 cases (nursing facility, behavioral health, physician visit, home care services)</li>
      <li><strong>Cross-payer schemes:</strong> 6 cases involved billing Medicare, Medicaid, and commercial insurers simultaneously</li>
      <li><strong>Average detection lag:</strong> 5.8 years from scheme start to prosecution</li>
      <li><strong>Phantom dispensing:</strong> The dominant fraud type — 7 of 8 pharmacy cases involved billing for medications never dispensed</li>
    </ul>
    <h3 className="subsection-title">Prevention Analysis:</h3>
    <p className="body-text">Of the 8 pharmacy fraud cases:</p>
    <ul className="bullet-list">
      <li><strong>7 cases ($48.3M confirmed)</strong> could have been prevented at the pre-payment stage by serialized reimbursement verification through real-time blocking of claims with no matching serialization event</li>
      <li><strong>1 case ($1.0M estimated)</strong> would likely have been detected significantly earlier through serialization pattern analysis flagging controlled substance chain-of-custody breaks</li>
    </ul>
    <h3 className="subsection-title">The Pattern:</h3>
    <p className="body-text">
      Michigan's pharmacy fraud profile demonstrates a consistent vulnerability: <strong>claims are paid based
      on billing data alone, with no verification that the physical drug was dispensed.</strong> The documented
      pharmacy fraud cases in Michigan exploited this verification gap. Serialized reimbursement
      infrastructure is designed to close the gap by requiring computational proof that a specific drug unit, with a
      verified serial number, was physically dispensed before authorizing payment.
    </p>
    <h2 className="main-section-title" style={{ marginTop: '2rem' }}>CASE ANALYSES</h2>
    <div className="case-block">
      <div className="case-header">
        <span className="case-number">CASE 1:</span>
        <span className="case-title">KOUZA BROTHERS — $15 MILLION (2010–2019)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Phantom dispensing</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicare, Medicaid, Blue Cross Blue Shield of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">2010–2019 (9 years undetected)</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">Eastern District of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Convicted November 2024; Sentenced November 2025</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Raad Kouza, a licensed pharmacist, and his brother Ramis Kouza, a pharmacy manager, operated pharmacies in Wayne and Oakland Counties, Michigan. Over a nine-year period, they systematically billed Medicare, Medicaid, and Blue Cross Blue Shield of Michigan for prescription medications that were never dispensed to patients.
      </p>
    </div>
    <div className="page-number">2</div>
  </section>
);

/* PDF Page 2 */
const Case1Detail = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <p className="body-text">
      The scheme targeted high-reimbursement medications — antipsychotics, inhalers, and other expensive drugs — where the margin between acquisition cost and reimbursement rate created maximum profit opportunity. The brothers concealed the fraud by manipulating pharmacy inventory records to hide shortages when auditors reviewed their operations. They created false documentation suggesting that drugs had been purchased and dispensed when, in reality, the medications never left the distributor or were diverted elsewhere.
    </p>
    <p className="body-text">
      The scheme operated across multiple pharmacies and involved systematic phantom dispensing: claims were filed, reimbursements were received, but patients never received the medications. The fraud was cross-payer — the same phantom dispensing operation billed all three payers simultaneously, extracting $15 million over nine years before federal investigators detected the pattern.
    </p>
    <p className="body-text">
      In November 2024, a federal jury in the Eastern District of Michigan convicted both brothers of conspiracy to commit health care fraud and wire fraud. In November 2025, Raad Kouza was sentenced to 8 years in federal prison, and Ramis Kouza received 5 years. The court ordered $15.5 million in restitution and forfeiture.
    </p>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      This case demonstrates the fundamental limitation of claims analytics: the billing data appeared legitimate. Prescriptions were written by licensed prescribers. The pharmacy was authorized. The drugs billed were appropriate for the patient populations. The claims passed all standard edits and were paid.
    </p>
    <p className="body-text">
      The fraud was invisible to claims-based detection because the claims themselves were structurally valid. What was missing was verification that the physical drug was dispensed. Auditors eventually detected inventory discrepancies — the pharmacy's purchase records did not match the volume of drugs billed — but this discovery came years into the scheme, after $15 million had been extracted.
    </p>
    <p className="body-text">
      The cross-payer nature of the fraud compounded the detection problem. Each payer (Medicare, Medicaid, BCBS) processed claims independently. No single payer saw the full scope of the scheme. Pattern analysis within one payer's data might flag anomalies, but the fraud was distributed across three separate claims systems, fragmenting the signal.
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      Under serialized reimbursement verification, this scheme is designed to fail at <strong>Step 3: Pharmacy Receipt Verification</strong>.
    </p>
    <p className="body-text">The six-step chain of custody for serialized reimbursement works as follows:</p>
    <ol className="numbered-list">
      <li><strong>Manufacturer Serial Number</strong> — Drug unit receives unique serial number at production</li>
      <li><strong>Wholesale Distribution</strong> — Serial number tracked through authorized distribution channels</li>
      <li><strong>Pharmacy Receipt</strong> — Pharmacy scans and verifies serial number upon receiving drug from distributor</li>
      <li><strong>Dispensing Event</strong> — Pharmacy records serial number at point of dispensing to patient</li>
      <li><strong>Prescriber Credential</strong> — Prescriber license validated against state board</li>
      <li><strong>Claim Submission</strong> — Claim must match a verified serial number dispensing event</li>
    </ol>
    <p className="body-text">
      When the Kouza Brothers filed a claim for a drug that was never dispensed, the claim would require a matching serialization event showing: the specific drug unit (serial number) was received by the pharmacy from an authorized distributor; the serial number was scanned and recorded at the point of dispensing; the dispensing event timestamp matches the claim date.
    </p>
    <div className="page-number">3</div>
  </section>
);

/* PDF Page 3 */
const Case1PreventionAndCase2Start = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <p className="body-text">
      If the drug was never dispensed, no serialization event exists. The claim is blocked before payment is authorized.
    </p>
    <p className="body-text">
      If the brothers attempted to reuse a serial number from a legitimately dispensed drug, the system would flag the duplicate — that serial number was already matched to a prior claim and cannot be billed again.
    </p>
    <p className="body-text">
      If they attempted to fabricate serial numbers, the verification system would reject them — those serial numbers do not exist in the DSCSA verification network or were never received by that pharmacy.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Pre-payment block at Step 3/4 — claim rejected due to missing or invalid serialization dispensing event.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: justice.gov/opa/pr/pharmacist-and-brother-sentenced-prison-15m-health-care-and-wire-fraud-scheme</span>
    </div>

    <div className="case-block" style={{ marginTop: '2rem' }}>
      <div className="case-header">
        <span className="case-number">CASE 2:</span>
        <span className="case-title">HAMAED / FAKHURI / GHUSSIN / ABDELRAZZAQ — $13 MILLION (FIVE PHARMACY RING)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Phantom dispensing (multi-pharmacy network)</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicare, Medicaid, Blue Cross Blue Shield of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">Multi-year conspiracy (exact dates unsealed; convicted September 2024)</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">Eastern District of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Convicted September 2024; Sentenced January–February 2025</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Four pharmacists — Raef Hamaed, Tarek Fakhuri, Ali Abdelrazzaq, and Kindy Ghussin — conspired to commit health care fraud and wire fraud across five pharmacies they owned and operated: Eastside Pharmacy, Harper Drugs, and Wayne Campus Pharmacy in Michigan, and Heartland Pharmacy and Heartland Pharmacy 2 in Ohio. The defendants billed Medicare, Medicaid, and Blue Cross Blue Shield of Michigan for prescription medications that they never dispensed.
      </p>
      <p className="body-text">
        This was not an isolated pharmacy operation — it was a coordinated multi-pharmacy network designed to maximize fraudulent billing volume while distributing the claims across multiple provider numbers to reduce detection risk. Each pharmacist had a defined role in the conspiracy across specific pharmacies:
      </p>
      <ul className="bullet-list">
        <li>Hamaed operated the conspiracy at all five pharmacies</li>
        <li>Fakhuri operated at Harper Drugs, Wayne Campus Pharmacy, and Heartland Pharmacy</li>
        <li>Ghussin operated at Wayne Campus Pharmacy and both Heartland pharmacies</li>
        <li>Abdelrazzaq operated at Wayne Campus Pharmacy</li>
      </ul>
      <p className="body-text">
        The conspiracy generated over $13 million in losses to Medicare, Medicaid, and Blue Cross Blue Shield of Michigan. On September 5, 2024, a federal jury convicted all four defendants of conspiracy to commit health care fraud and wire fraud. Fakhuri was additionally convicted of one count of health care fraud.
      </p>
      <p className="body-text">
        Sentencing occurred in early 2025: Hamaed received 10 years in prison (the ringleader); Fakhuri received 7 years; Ghussin received 5 years and 5 months; and Abdelrazzaq received 2 years.
      </p>
    </div>
    <div className="page-number">4</div>
  </section>
);

/* PDF Page 4 */
const Case2WhyAndPrevention = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      The multi-pharmacy structure was specifically designed to evade detection. By distributing fraudulent claims across five pharmacies in two states (Michigan and Ohio), the conspirators ensured that no single pharmacy's billing volume appeared anomalous enough to trigger automated fraud alerts.
    </p>
    <p className="body-text">
      Each pharmacy's claim volume might fall within expected parameters when reviewed individually. The connection between the pharmacies — shared ownership by co-conspirators — was not apparent in claims data. The Medicare, Medicaid, and BCBS claims systems each processed their portion of the fraud independently, with no cross-reference between payers or between pharmacy locations.
    </p>
    <p className="body-text">
      Claims analytics can detect outliers within a single pharmacy's billing pattern. It cannot verify that the drugs billed were physically dispensed. The fraud was structured to stay within statistical norms while generating maximum fraudulent revenue.
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      Under serialized reimbursement verification, this scheme is designed to fail at <strong>Step 4: Dispensing Event Verification</strong>.
    </p>
    <p className="body-text">
      Regardless of how many pharmacies the conspirators operated, each claim must be matched to a unique serialization dispensing event. The drugs billed were never dispensed — therefore, no serialization events exist for those claims.
    </p>
    <p className="body-text">
      The multi-pharmacy structure provides no advantage under serialization verification. Five pharmacies submitting unverified claims generate five independent red flags, not distributed camouflage. The system verifies each claim individually against DSCSA serialization data.
    </p>
    <p className="body-text">
      Additionally, serialization cross-referencing would detect if the same serial numbers were being used across multiple pharmacy locations — a pattern that would immediately flag coordination between the pharmacies and trigger investigation far earlier than traditional claims analytics.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Pre-payment block at Step 4 — claims from all five pharmacies rejected due to absent serialization dispensing events.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: justice.gov/opa/pr/four-pharmacists-sentenced-roles-13m-medicare-medicaid-and-private-insurer-fraud-conspiracy</span>
    </div>
    <div className="case-block" style={{ marginTop: '2rem' }}>
      <div className="case-header">
        <span className="case-number">CASE 3:</span>
        <span className="case-title">LIFECARE PHARMACY / MOHAMAD MAKKI — $9.8 MILLION (2013–2019)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Phantom dispensing with invoice fabrication</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicare, Medicaid, Blue Cross Blue Shield of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">2013–2019 (6 years undetected)</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">Eastern District of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Sentenced January 2022 (77 months in prison)</span></div>
      </div>
    </div>
    <div className="page-number">5</div>
  </section>
);

/* PDF Page 5 */
const Case3WhatHappened = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h4 className="case-subsection">What Happened:</h4>
    <p className="body-text">
      Mohamad Ali Makki, a licensed pharmacist, owned and operated LifeCare Pharmacy in Farmington Hills and LifeCare of Michigan, also in the Detroit metro area. From 2013 through 2019, Makki, together with co-conspirators Wansa Makki and Hossam Tanana (Wansa's husband), ran a systematic scheme to bill Medicare, Medicaid, and Blue Cross Blue Shield of Michigan for approximately $9.2 million in prescription medications that were never purchased or dispensed.
    </p>
    <p className="body-text">
      The scheme was notable for its sophistication in concealment. When auditors examined the pharmacies, Makki created false invoices to fabricate evidence that LifeCare had purchased the pharmaceuticals it claimed to have dispensed. This fraudulent paper trail was designed to defeat exactly the kind of post-hoc audit that is the primary integrity mechanism for pharmacy claims.
    </p>
    <p className="body-text">
      Proceeds from the fraud were laundered through consulting and delivery companies operated by Tanana and Wansa's brother, Mahmoud Makki. These shell entities created additional layers of financial separation between the fraudulent claims and the ultimate beneficiaries.
    </p>
    <p className="body-text">
      The scheme was eventually flagged by Medicare auditors who identified a "huge deficit" between each pharmacy's actual inventory purchases and the volume of claims submitted — the pharmacies were billing for far more drugs than they could possibly have obtained through legitimate distribution channels.
    </p>
    <p className="body-text">
      Mohamad Makki was sentenced to 77 months in federal prison, ordered to pay $9.8 million in restitution, and required to forfeit approximately $1.1 million in seized bank funds.
    </p>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      This case demonstrates a failure at multiple levels. First, the basic claims system paid every phantom claim without physical verification. Second, when auditors attempted post-hoc review, the defendant created fabricated invoices to defeat the audit — converting a purchase records check from a fraud detection tool into a fraud concealment opportunity.
    </p>
    <p className="body-text">
      The inventory-to-claims gap that eventually triggered detection required years of accumulated data and manual auditor review to identify. The defendant's fabricated invoices successfully delayed detection further by creating a false paper trail that auditors initially accepted.
    </p>
    <p className="body-text">
      The fraud crossed three payers. Each payer's audit function examined its own claims independently. The aggregate $9.8 million loss was distributed across Medicare, Medicaid, and BCBS — no single payer bore the full loss or saw the full picture.
    </p>
    <div className="page-number">6</div>
  </section>
);

/* PDF Page 6 */
const Case3PreventionAndCase4 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      Under serialized reimbursement verification, this scheme is designed to fail at <strong>Step 3: Pharmacy Receipt Verification</strong>.
    </p>
    <p className="body-text">
      Fabricated invoices cannot fool serialization verification. DSCSA serialization data is generated by the manufacturer at production and tracked through every authorized distributor. The data does not originate at the pharmacy level — it flows from upstream in the supply chain.
    </p>
    <p className="body-text">
      When Makki submitted a claim for a drug never purchased, the serialization system would check: (a) does a valid serial number exist for this drug? (b) was it shipped by an authorized distributor to this pharmacy? (c) was it scanned as received at this pharmacy? (d) was it scanned as dispensed?
    </p>
    <p className="body-text">
      Fabricated paper invoices cannot insert serial numbers into the DSCSA verification network. The data is cryptographically tracked from manufacturer through distribution. A pharmacy cannot fabricate upstream supply chain events.
    </p>
    <p className="body-text">
      The money laundering through shell companies becomes irrelevant — the fraudulent claims would never have been paid in the first place.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Pre-payment block at Step 3 — claim rejected because no matching DSCSA distribution record confirms the drug was shipped to and received by the pharmacy.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: justice.gov/usao-edmi/pr/pharmacy-owner-sentenced-role-9-million-scheme-bill-insurance-medications-not-dispensed</span>
    </div>

    <div className="case-block" style={{ marginTop: '2rem' }}>
      <div className="case-header">
        <span className="case-number">CASE 4:</span>
        <span className="case-title">MAKKI / NEW MILLENNIUM DRUGS / WESTERN WAYNE PHARMACY — $6.9 MILLION (JUNE 2025)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Phantom dispensing (cross-payer)</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicare, Medicaid, Commercial insurers</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">Multi-year (exact dates from indictment)</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">Eastern District of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Prosecuted as part of June 2025 National Takedown</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Mohammed Makki and associated pharmacies — including New Millennium Drugs and Western Wayne Pharmacy in the Dearborn Heights area — submitted false claims to Medicare, Medicaid, and commercial insurers totaling $6.9 million for prescription drugs that were billed but never dispensed. The case was prosecuted as part of the Eastern District of Michigan's contribution to the June 2025 national healthcare fraud enforcement action.
      </p>
      <p className="body-text">
        This case is connected to the broader pattern of pharmacy fraud in the Metro Detroit area, where multiple pharmacy operations have been identified as submitting claims for medications never provided to patients. The cross-payer billing pattern — hitting Medicare, Medicaid, and commercial insurers simultaneously — is consistent with the operating method of the documented major pharmacy fraud cases in Michigan.
      </p>
    </div>
    <div className="page-number">7</div>
  </section>
);

/* PDF Page 7 (Case 4 continued + Case 5 start) */
const Case4PreventionAndCase5Start = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      The verification gap operated identically to the other phantom dispensing cases: claims were structurally valid, passed standard edits, and were paid by each payer independently. No payer checked whether the drug was physically dispensed. No payer cross-referenced claims with the other payers to identify the same phantom operation billing all three simultaneously.
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      Under serialized reimbursement verification, this scheme is designed to fail at <strong>Step 4: Dispensing Event Verification</strong>.
    </p>
    <p className="body-text">
      Claims for $6.9 million in drugs never dispensed would generate $6.9 million in blocked payments — each claim rejected at the point of submission because no serialization dispensing event confirms the drug was physically dispensed to a patient.
    </p>
    <p className="body-text">
      The cross-payer nature of the fraud is directly addressed by serialization's payer-agnostic architecture: the same verification that blocks a fraudulent Medicaid claim blocks the corresponding Medicare and commercial claims for the same phantom dispensing event.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Pre-payment block at Step 4 — all claims rejected across all three payers due to absent serialization dispensing events.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: DOJ USAO-EDMI, civil resolution, June 30, 2025; part of 2025 National Health Care Fraud Enforcement Action.</span>
    </div>

    <div className="case-block" style={{ marginTop: '2rem' }}>
      <div className="case-header">
        <span className="case-number">CASE 5:</span>
        <span className="case-title">NABIL FAKIH — $4 MILLION (2011–2017)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Phantom dispensing with inventory manipulation</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicare</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">2011–2017 (6 years undetected)</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">Eastern District of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Pleaded guilty August 2024; Sentenced November 2025 (46 months)</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Nabil Fakih, 50, owned and operated a pharmacy in Dearborn Heights, Michigan. From 2011 to 2017, Fakih billed Medicare for prescription medications that he never dispensed. The scheme specifically targeted high-reimbursement medications — blood thinners and lung disease inhalers — where the reimbursement rates generated maximum profit per phantom claim.
      </p>
      <p className="body-text">
        Fakih's scheme was notable for a specific characteristic: his pharmacy did not even have the inventory to dispense the medications he was billing for. He was submitting claims for drugs that his pharmacy had never purchased. To conceal this, Fakih manipulated the inventory purchase records at his pharmacy, creating false documentation of purchases that never occurred. He also manipulated the receipt and transfer of fraud proceeds, diverting funds for personal use.
      </p>
      <p className="body-text">
        The total loss to Medicare was approximately $4 million. In August 2024, Fakih pleaded guilty to one count of health care fraud in the Eastern District of Michigan. In November 2025, he was sentenced to 46 months in prison, ordered to pay $4 million in restitution, and required to forfeit four real estate properties and $726,364.96 in assets.
      </p>
    </div>
    <div className="page-number">8</div>
  </section>
);

/* PDF Page 8 (Case 5 Why/Prevention + Case 6 full) */
const Case5PreventionAndCase6 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      Medicare's claims system processed Fakih's claims without verifying that his pharmacy had the inventory to fill the prescriptions billed. The claims appeared legitimate on their face — valid NPI, valid patient, valid drug code, valid prescription. Every standard claims edit was satisfied.
    </p>
    <p className="body-text">
      The inventory manipulation extended the fraud's lifespan by defeating the primary post-hoc detection mechanism: purchase record audits. When investigators eventually examined Fakih's operations, the fabricated inventory records initially obscured the disconnect between what was purchased and what was billed.
    </p>
    <p className="body-text">
      This is a single-payer variant of the verification gap: Medicare Part D paid claims for drugs that physically did not exist at the pharmacy. The billing system had no mechanism to ask: "Does this pharmacy actually have this drug?"
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      Under serialized reimbursement verification, this scheme is designed to fail at <strong>Step 3: Pharmacy Receipt Verification</strong>.
    </p>
    <p className="body-text">
      Fakih's pharmacy never purchased the blood thinners and inhalers he billed for. Under DSCSA, those drugs would have serial numbers assigned at manufacture and tracked through distribution. For Fakih to bill for a specific drug, the verification system would require proof that: a drug with a valid serial number was shipped by a distributor to his pharmacy; his pharmacy scanned and confirmed receipt of that serial number; the serial number was recorded as dispensed to a patient.
    </p>
    <p className="body-text">
      None of these events occurred. Manipulated paper records cannot inject data into the DSCSA serialization network. The first fraudulent claim would be blocked.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Pre-payment block at Step 3 — claim rejected because no DSCSA distribution/receipt record confirms the drug was ever shipped to or received by Fakih's pharmacy.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: justice.gov/opa/pr/michigan-pharmacist-sentenced-46-months-prison-4m-health-care-fraud-scheme</span>
    </div>

    <div className="case-block" style={{ marginTop: '2rem' }}>
      <div className="case-header">
        <span className="case-number">CASE 6:</span>
        <span className="case-title">KIRTLAND CORP / WESTERN WAYNE PHARMACY — $1.5 MILLION (JUNE 2025)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Phantom dispensing</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicare, Medicaid</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">Multi-year operation</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">Eastern District of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">$1.5 million FCA settlement; 10-year exclusion for Wahid Makki (June 2025)</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Wahid Makki, 62, and Zainab Makki, 62, of Dearborn Heights, Michigan, operated Kirtland Corp (doing business as New Millennium Drugs) and Western Wayne Pharmacy LLC. They submitted false claims to Medicare and Medicaid for prescription drugs that were never dispensed to patients.
      </p>
      <p className="body-text">
        The case was resolved through a False Claims Act civil settlement as part of the June 2025 national healthcare fraud enforcement action. The Makkis agreed to pay $1.5 million to resolve the allegations. Wahid Makki was excluded from participation in all federal health care programs for 10 years — a significant penalty that bars him from billing Medicare or Medicaid in any capacity.
      </p>
    </div>
    <div className="page-number">9</div>
  </section>
);

/* PDF Page 9 (Case 6 Why/Prevention + Case 7 start) */
const Case6PreventionAndCase7Start = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      The standard verification gap: claims were submitted, claims passed edits, claims were paid. No mechanism existed to verify that the drugs billed were physically dispensed. The phantom dispensing was detected through post-hoc investigation rather than real-time prevention.
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      Under serialized reimbursement verification, this scheme is designed to fail at <strong>Step 4: Dispensing Event Verification</strong>.
    </p>
    <p className="body-text">
      Every claim for a drug "dispensed" by Western Wayne Pharmacy or New Millennium Drugs would require a matching DSCSA serialization event. No dispensing occurred; no serialization event exists; claim blocked.
    </p>
    <p className="body-text">
      The $1.5 million extracted over the scheme's duration represents the cumulative cost of paying claims without physical verification. Under serialized verification, the first claim would likely have been flagged, creating a pre-payment control designed to block the scheme before generating losses.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Pre-payment block at Step 4 — claims rejected due to absent dispensing verification events.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: DOJ USAO-EDMI, civil resolution, June 30, 2025.</span>
    </div>

    <div className="case-block" style={{ marginTop: '2rem' }}>
      <div className="case-header">
        <span className="case-number">CASE 7:</span>
        <span className="case-title">P & A AFTERCARE / DETROIT HOOVER PHARMACY — CONTROLLED SUBSTANCE DIVERSION (JUNE 2025)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Controlled substance diversion / fraudulent prescribing</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicare, Medicaid (via fraudulent prescriptions for controlled substances)</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">Multi-year operation</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">Eastern District of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Four defendants charged (June 2025); Part of Operation Gold Rush</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Four defendants were charged as part of the June 2025 national healthcare fraud takedown in a controlled substance diversion ring operating across two locations in Metro Detroit:
      </p>
      <ul className="bullet-list">
        <li>Durand Bynum, 46, Canton, MI</li>
        <li>Ebony Daniels, 33, Eastpointe, MI</li>
        <li>Allen Satawhite, 37, Detroit, MI</li>
        <li>Usman Ahmad, 66, Lake Orion, MI — owner of Detroit Hoover Pharmacy</li>
      </ul>
      <p className="body-text">
        The scheme operated through P & A Aftercare, a treatment facility in Southfield, and Detroit Hoover Pharmacy in Detroit. Doctors at P & A Aftercare were hired to write prescriptions for controlled substances — Oxycodone, Percocet, and Norco — for fictitious patients without medical necessity. Ahmad, as owner of Detroit Hoover Pharmacy, used the pharmacy to fill these fraudulent prescriptions. The drugs were obtained through legitimate distribution channels but dispensed to fictitious patients or diverted for street sale.
      </p>
      <p className="body-text">
        The case was prosecuted as part of the DOJ Criminal Division's Midwest Strike Force "Operation Gold Rush" — a targeted operation against transnational criminal organizations that submitted more than $12 billion in fraudulent claims to Medicare nationally.
      </p>
    </div>
    <div className="page-number">10</div>
  </section>
);

/* PDF Page 9-10 (Case 7 Why/Prevention) */
const Case7WhyAndPrevention = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      This case presents a different verification failure than phantom dispensing. Here, drugs were physically obtained and may have been logged as dispensed — but they were dispensed to fictitious patients or diverted rather than provided to legitimate beneficiaries.
    </p>
    <p className="body-text">
      Claims analytics might detect volume anomalies (a single pharmacy filling an unusual number of controlled substance prescriptions), but the claims themselves appeared valid: real prescriptions, written by licensed physicians, for drugs that existed in the pharmacy's inventory. The Prescription Drug Monitoring Program (PDMP) can flag patient-level controlled substance patterns but does not verify the physical chain of custody for each drug unit.
    </p>
    <p className="body-text">
      The convergence of a fraudulent prescriber (P & A Aftercare doctors) and a complicit dispensing pharmacy (Detroit Hoover) created a closed loop that defeated both provider screening and claims-level detection.
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      Under serialized reimbursement verification, this scheme is detectable at <strong>Step 4: Dispensing Event Verification</strong> through pattern analysis.
    </p>
    <p className="body-text">
      While the drugs in this case may have been physically present at the pharmacy (unlike phantom dispensing), serialized verification adds critical layers:
    </p>
    <ol className="numbered-list">
      <li><strong>Patient verification crossmatch:</strong> Serialization records showing dispensing to patients who have no corresponding medical records, no other prescription history, or who match known fictitious identity patterns trigger real-time flags.</li>
      <li><strong>Prescriber concentration analysis:</strong> When serialization data shows that a single prescriber (P & A Aftercare doctors) accounts for an anomalous percentage of controlled substance dispensing events at a specific pharmacy (Detroit Hoover), the system flags this pattern in real time — not years later through retrospective claims review.</li>
      <li><strong>Chain-of-custody anomalies:</strong> Controlled substances flowing from manufacturer to distributor to pharmacy to fictitious patient create serialization records with no downstream verification. The drugs' serial numbers never appear in any subsequent healthcare event (refill, medication reconciliation, dosage adjustment), creating a dead-end pattern that serialization analytics identify as high-risk.</li>
      <li><strong>Volume-to-verification mismatch:</strong> An unusually high ratio of controlled substance serialization events at one pharmacy, from one prescriber, for patients with no other verified serialization dispensing history, generates automated investigation triggers.</li>
    </ol>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Real-time flag at Step 4 — serialization pattern analysis detects anomalous prescriber-pharmacy-patient concentration and fictitious patient indicators, triggering investigation within weeks rather than years.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: justice.gov/usao-edmi/pr/national-health-care-fraud-enforcement-action-results-324-defendants-charged-and-over</span>
    </div>
    <div className="page-number">11</div>
  </section>
);

/* PDF Page 10-11 (Case 8) */
const Case8 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <div className="case-block">
      <div className="case-header">
        <span className="case-number">CASE 8:</span>
        <span className="case-title">DIVINE PHARMACY / REMIGIUS ONIMO — UNAUTHORIZED PRACTICE + MEDICAID FRAUD (2020–2021)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Unauthorized practice / Medicaid false claims</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicaid</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">2020–2021 (dispensing on suspended license)</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">State — 30th Circuit Court, Ingham County</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Charged April 2025; Bound over for trial May 2025</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Remigius U. Onimo, 64, of Solon, Ohio, owned Divine Pharmacy on Livernois Avenue in Detroit. Onimo held a pharmacist license in Michigan, but in January 2020, his license was suspended. Despite the suspension, Onimo allegedly continued to fill prescriptions throughout 2020 and 2021 — including prescriptions billed to and paid by Medicaid.
      </p>
      <p className="body-text">
        The case represents a different fraud vector: an unlicensed provider billing a government payer. Onimo was charged with one count of Health Profession-Unauthorized Practice (felony, up to 4 years) and two counts of Medicaid Fraud-False Claim (felony, up to 4 years and/or $50,000 per count). Attorney General Nessel's office prosecuted through the Health Care Fraud Division.
      </p>
    </div>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      Medicaid's provider credentialing system did not detect or prevent claims submission from a pharmacist with a suspended license. The claims appeared to come from Divine Pharmacy — a registered provider — but the individual dispensing medications was not authorized to practice.
    </p>
    <p className="body-text">
      This failure sits at the intersection of provider screening and claims processing: the state licensing board suspended Onimo's license, but this information was not propagated to or enforced by the Medicaid claims system in real time. Claims continued to be submitted and paid for over a year after the license suspension.
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      Under serialized reimbursement verification, this scheme is detectable at <strong>Step 5: Prescriber/Dispenser Credential Verification</strong>.
    </p>
    <p className="body-text">
      The six-step chain of custody includes real-time validation of the dispensing pharmacist's credential status. When Onimo's license was suspended in January 2020, serialized verification would flag any dispensing event associated with his pharmacist ID:
    </p>
    <ul className="bullet-list">
      <li>Every DSCSA dispensing event must be logged by an authorized dispenser</li>
      <li>The dispenser's credential is validated against state licensing data at the time of each dispensing event</li>
      <li>A suspended license triggers an immediate block: the dispensing event is invalid, and any associated claim is rejected</li>
    </ul>
    <p className="body-text">
      This moves credential enforcement from a periodic re-enrollment check to a real-time, per-transaction validation.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Pre-payment block at Step 5 — dispensing event rejected because the pharmacist's license is suspended; associated Medicaid claim cannot be submitted.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: michigan.gov/ag/news/press-releases/2025/05/29/former-detroit-pharmacist-to-stand-trial-for-medicaid-fraud</span>
    </div>
    <div className="page-number">12</div>
  </section>
);

/* PDF Pages 11-12 (Case 9 Villa Nursing Homes) */
const Case9 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <div className="case-block">
      <div className="case-header">
        <span className="case-number">CASE 9:</span>
        <span className="case-title">VILLA NURSING HOMES — $4.5 MILLION (JUNE 2025)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">False claims for services not rendered (nursing facility)</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicare, Medicaid</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">Multi-year systematic deficiencies</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">Eastern District of Michigan</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">$4.5 million FCA settlement; 5-year Corporate Integrity Agreement (June 2025)</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Six Southeast Michigan nursing facilities operating under the Villa brand — The Ambassador, Father Murray, Imperial, Regency, St. Joseph's, and Westland — agreed to pay $4.5 million to resolve False Claims Act allegations. The facilities, operated by Villa Financial Services LLC and Villa Olympia Investment LLC, systematically failed to provide services to residents while billing Medicare and Medicaid for those services.
      </p>
      <p className="body-text">
        The deficiencies included grossly substandard care, inadequate staffing ratios, failure in infection control protocols, and insufficient fall prevention programs. Residents were billed for levels of care they did not receive.
      </p>
      <p className="body-text">
        The resolution included a five-year Corporate Integrity Agreement with HHS-OIG and a required quality monitor — the most stringent compliance remedies available in civil healthcare fraud resolution.
      </p>
    </div>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      This case involves services fraud (billing for care not provided) rather than pharmaceutical fraud. Claims analytics can flag billing anomalies, but verifying that the services billed were actually provided to patients requires on-site inspection — a resource-intensive process that cannot scale.
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      <strong>This case falls outside the direct scope of serialized reimbursement verification</strong>, as it involves nursing care services rather than pharmaceutical dispensing. However, the pharmaceutical component of nursing home care — medication administration to residents — intersects with serialization in one important way:
    </p>
    <p className="body-text">
      To the extent that Villa facilities billed for medications administered to residents, serialization verification would ensure that the drugs were physically present and dispensed. If medications were billed but not administered (a common component of nursing facility fraud), the absence of serialization dispensing events for those medications would flag discrepancies.
    </p>
    <p className="body-text">
      Serialized verification is not a substitute for care quality monitoring, but it provides an independent data stream that can detect pharmaceutical billing fraud within nursing facilities.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Partial — serialization verification can detect pharmaceutical billing fraud within nursing facilities but does not address care quality or staffing deficiencies. Primary prevention for this fraud type requires quality monitoring infrastructure.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: DOJ USAO-EDMI, civil resolution, June 30, 2025.</span>
    </div>
    <div className="page-number">13</div>
  </section>
);

/* PDF Page 12-13 (Case 10 Oxford Recovery) */
const Case10 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <div className="case-block">
      <div className="case-header">
        <span className="case-number">CASE 10:</span>
        <span className="case-title">OXFORD RECOVERY CENTER / TAMELA PETERSON — HEALTHCARE FRAUD (JANUARY 2026)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">False claims for behavioral health and addiction treatment services</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicaid (and potentially commercial insurers)</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">Investigation initiated September 2022</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">State — 53rd District Court, Howell (Livingston County)</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Charged January 2026 (9 felony counts); Pending trial</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Tamela Peterson, 59, of Brighton, Michigan, the former CEO of Oxford Recovery Center (Brighton) and Oxford Center (Troy), was charged with nine felony counts of Health Care Fraud — False Claims. The facilities provided addiction treatment, mental health services, and applied behavioral analysis (ABA) programs for children with autism.
      </p>
      <p className="body-text">
        Peterson allegedly knew of and directly benefited from fraudulent billing — charging for services that were never rendered to patients. Each count carries up to 4 years in prison and/or a $50,000 fine.
      </p>
      <p className="body-text">
        The case is notable for its context: Peterson was already facing second-degree murder and involuntary manslaughter charges related to the January 2025 death of 5-year-old Thomas Cooper in a hyperbaric chamber at one of the facilities.
      </p>
    </div>
    <h4 className="case-subsection">Why Current Systems Failed:</h4>
    <p className="body-text">
      Behavioral health and addiction treatment billing is inherently difficult to verify through claims data alone. Sessions, evaluations, and therapeutic services are documented by the provider and billed based on provider records. Unlike pharmaceutical dispensing, there is no physical product to track.
    </p>
    <h4 className="case-subsection">How MENDRx Prevents This:</h4>
    <p className="body-text">
      <strong>This case falls outside the direct scope of serialized reimbursement verification</strong>, as it involves behavioral health services rather than pharmaceutical dispensing. However, to the extent that Oxford Recovery Center administered medications as part of addiction treatment (MAT programs, for example), serialization would verify that the pharmaceutical component of treatment was legitimate.
    </p>
    <div className="prevention-box">
      <strong>Prevention Mechanism:</strong> Not directly applicable — services fraud in behavioral health requires different verification infrastructure. Pharmaceutical component of MAT programs would be verifiable through serialization.
    </div>
    <div className="source-link">
      <ExternalLink size={14} />
      <span>Source: michigan.gov/ag/news/press-releases/2026/01/20/attorney-general-nessel-charges-former-ceo-of-oxford-recovery</span>
    </div>
    <div className="page-number">14</div>
  </section>
);

/* PDF Page 13-14 (Cases 11 + 12) */
const Cases11And12 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <div className="case-block">
      <div className="case-header">
        <span className="case-number">CASE 11:</span>
        <span className="case-title">DR. JAMES CARTHRON — $11,000 MEDICAID FRAUD (AUGUST 2025)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Phantom telephone visits</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicaid</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">May 31 – September 27, 2024 (4 months)</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">State — Michigan AG prosecution</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Charged August 2025 (23 counts Medicaid fraud)</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        James A. Carthron, M.D., 61, of Saginaw, Michigan, was charged with 23 counts of Medicaid Fraud — False Claim. Carthron allegedly billed Medicaid approximately $11,000 for 23 telephone evaluation and management visits to three beneficiaries that never occurred. The phantom visits were billed over a four-month period from May to September 2024.
      </p>
      <h4 className="case-subsection">Why Current Systems Failed:</h4>
      <p className="body-text">
        Telehealth billing verification is a known vulnerability. Claims for telephone visits are difficult to verify because they leave no physical trail — no office visit record, no in-person documentation, no physical evidence of a provider-patient interaction. The expansion of telehealth billing during and after the COVID-19 pandemic significantly expanded this attack surface.
      </p>
      <h4 className="case-subsection">How MENDRx Prevents This:</h4>
      <p className="body-text">
        <strong>This case falls outside the direct scope of serialized reimbursement verification</strong>, as it involves physician evaluation and management services rather than pharmaceutical dispensing. However, this case illustrates the broader verification gap principle: when payment systems rely on provider attestation without independent verification, fraud becomes possible. Serialized reimbursement verification addresses this gap for pharmaceutical claims by requiring physical evidence (serialization data) before payment.
      </p>
      <div className="prevention-box">
        <strong>Prevention Mechanism:</strong> Not directly applicable to physician visit fraud. Demonstrates the verification gap principle that MENDRx addresses for pharmaceutical claims.
      </div>
      <div className="source-link">
        <ExternalLink size={14} />
        <span>Source: michigan.gov/ag/news/press-releases/2025/08/15/saginaw-physician-charged-with-medicaid-fraud</span>
      </div>
    </div>

    <div className="case-block" style={{ marginTop: '2rem' }}>
      <div className="case-header">
        <span className="case-number">CASE 12:</span>
        <span className="case-title">DALEENA MILLER — $8,640 MEDICAID FRAUD (JUNE 2025)</span>
      </div>
      <div className="case-meta-grid">
        <div className="case-meta-item"><span className="meta-label">Fraud Type:</span><span className="meta-value">Community Living Support (CLS) false documentation</span></div>
        <div className="case-meta-item"><span className="meta-label">Payers Defrauded:</span><span className="meta-value">Medicaid</span></div>
        <div className="case-meta-item"><span className="meta-label">Duration:</span><span className="meta-value">Reported as part of June 2025 national takedown</span></div>
        <div className="case-meta-item"><span className="meta-label">District:</span><span className="meta-value">State — Michigan AG prosecution</span></div>
        <div className="case-meta-item"><span className="meta-label">Status:</span><span className="meta-value">Charged June 2025 (Medicaid Fraud False Claim)</span></div>
      </div>
      <h4 className="case-subsection">What Happened:</h4>
      <p className="body-text">
        Daleena T. Miller, 42, of Battle Creek, Michigan, was a Community Living Support (CLS) worker who placed false information on medical charts to bill Medicaid $8,640 for services not rendered. Miller documented more hours of service than she actually provided, fabricating records to support the inflated billing.
      </p>
      <h4 className="case-subsection">Why Current Systems Failed:</h4>
      <p className="body-text">
        Home and community-based service billing relies on worker self-reporting and supervisor attestation. Time-and-attendance verification for CLS workers is inherently difficult, particularly in home-based settings where direct supervision is limited.
      </p>
      <h4 className="case-subsection">How MENDRx Prevents This:</h4>
      <p className="body-text">
        <strong>This case falls outside the direct scope of serialized reimbursement verification</strong>, as it involves personal care services rather than pharmaceutical dispensing.
      </p>
      <div className="prevention-box">
        <strong>Prevention Mechanism:</strong> Not directly applicable. Home and community-based service fraud requires time-and-attendance verification infrastructure, not pharmaceutical serialization.
      </div>
      <div className="source-link">
        <ExternalLink size={14} />
        <span>Source: MLive, July 1, 2025.</span>
      </div>
    </div>
    <div className="page-number">15</div>
  </section>
);

/* PDF Page 15 (Pattern Analysis) */
const PatternAnalysis1 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">PATTERN ANALYSIS</h2>

    <h3 className="subsection-title">Fraud Type Distribution</h3>
    <p className="body-text">Of the 12 cases analyzed:</p>
    <div className="pattern-box">
      <h4>Pharmacy/Pharmaceutical Fraud (8 cases, $49.3M):</h4>
      <ul className="bullet-list">
        <li>Phantom dispensing: 7 cases ($48.3M)</li>
        <li>Controlled substance diversion: 1 case ($1.0M estimated exposure)</li>
      </ul>
    </div>
    <div className="pattern-box">
      <h4>Non-Pharmaceutical Fraud (4 cases, $1.5M):</h4>
      <ul className="bullet-list">
        <li>Nursing facility services fraud: 1 case ($4.5M)</li>
        <li>Behavioral health services fraud: 1 case (amount TBD)</li>
        <li>Physician visit fraud: 1 case ($11K)</li>
        <li>Home care services fraud: 1 case ($8.6K)</li>
      </ul>
    </div>
    <div className="key-finding">
      <strong>Key Finding:</strong> Pharmacy fraud represents the overwhelming majority of the total dollar value across all Michigan cases analyzed. Non-pharmacy fraud cases (nursing care, behavioral health, physician visits, home care) totaled $4.5M+ in confirmed amounts.
    </div>

    <h3 className="subsection-title">Detection Lag Analysis</h3>
    <p className="body-text"><strong>Average time from scheme start to prosecution: 5.8 years</strong></p>
    <ul className="bullet-list">
      <li>Kouza Brothers: 9 years (2010–2019)</li>
      <li>LifeCare Pharmacy / Makki: 6 years (2013–2019)</li>
      <li>Nabil Fakih: 6 years (2011–2017)</li>
      <li>Hamaed/Fakhuri ring: Multi-year (exact duration unsealed)</li>
      <li>P & A Aftercare: Multi-year operation</li>
      <li>Divine Pharmacy / Onimo: 1–2 years (2020–2021, charged 2025)</li>
    </ul>
    <div className="key-finding">
      <strong>Key Finding:</strong> The average pharmacy fraud scheme in Michigan operated for nearly six years before prosecution. During this detection lag, fraudulent payments continued to flow, and schemes expanded in scope.
    </div>

    <h3 className="subsection-title">Cross-Payer Pattern</h3>
    <p className="body-text">6 of 8 pharmacy fraud cases involved billing multiple payers simultaneously:</p>
    <ol className="numbered-list">
      <li>Kouza Brothers — Medicare, Medicaid, BCBS Michigan</li>
      <li>Hamaed/Fakhuri ring — Medicare, Medicaid, BCBS Michigan</li>
      <li>LifeCare / Makki — Medicare, Medicaid, BCBS Michigan</li>
      <li>Makki / New Millennium — Medicare, Medicaid, commercial</li>
      <li>Nabil Fakih — Medicare (single payer)</li>
      <li>Kirtland / Western Wayne — Medicare, Medicaid</li>
      <li>P & A Aftercare — Medicare, Medicaid (via controlled substance prescriptions)</li>
      <li>Divine Pharmacy — Medicaid (single payer)</li>
    </ol>
    <div className="key-finding">
      <strong>Key Finding:</strong> Cross-payer fraud is the dominant pattern. Fraudsters do not target one payer — they bill all available payers for the same phantom operation, maximizing revenue and fragmenting detection across multiple claims systems.
    </div>

    <h3 className="subsection-title">Geographic Concentration</h3>
    <p className="body-text">All 8 pharmacy fraud cases occurred in the Detroit metropolitan area, specifically: Wayne County (Detroit, Dearborn Heights, Livonia, Farmington Hills), Oakland County (Troy, Lake Orion), and Macomb County.</p>
    <div className="key-finding">
      <strong>Key Finding:</strong> Michigan's pharmacy fraud is geographically concentrated in Metro Detroit, where the Eastern District of Michigan's Health Care Fraud Strike Force maintains permanent operations.
    </div>
    <div className="page-number">16</div>
  </section>
);

/* PDF Page 16 (Drug Categories + Chain of Custody + Total Exposure + Summary Table start) */
const PatternAnalysis2AndSummaryStart = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h3 className="subsection-title">Drug Categories Targeted</h3>
    <p className="body-text">High-reimbursement medications consistently targeted across cases:</p>
    <ul className="bullet-list">
      <li>Antipsychotics (Kouza Brothers)</li>
      <li>Inhalers / respiratory medications (Kouza Brothers, Fakih)</li>
      <li>Blood thinners (Fakih)</li>
      <li>Controlled substances (P & A Aftercare: Oxycodone, Percocet, Norco)</li>
      <li>HIV medications (referenced in related national cases)</li>
    </ul>
    <div className="key-finding">
      <strong>Key Finding:</strong> Fraudsters target drugs with the highest reimbursement rates and, in the case of controlled substances, street value. These are precisely the drug categories where serialization verification delivers maximum fraud prevention ROI.
    </div>

    <h3 className="subsection-title">Chain of Custody Failure Points</h3>
    <p className="body-text">Analyzing where each scheme would likely have been blocked under serialized verification:</p>
    <ul className="bullet-list">
      <li><strong>Step 3 (Pharmacy Receipt): 3 cases</strong> — drugs never purchased/received by pharmacy</li>
      <li><strong>Step 4 (Dispensing Event): 4 cases</strong> — drugs never dispensed to patients</li>
      <li><strong>Step 5 (Credential Validation): 1 case</strong> — dispensing by unlicensed pharmacist</li>
      <li><strong>Pattern Analysis (Real-time flags): 1 case</strong> — controlled substance diversion detected via anomaly patterns</li>
    </ul>
    <div className="key-finding">
      <strong>Key Finding:</strong> The majority of fraud (7 of 8 cases) could have been blocked at the pre-payment stage through missing or invalid serialization data. One case would likely have been flagged significantly earlier through real-time pattern analysis.
    </div>

    <h3 className="subsection-title">Total Preventable Exposure</h3>
    <p className="body-text">Pharmacy fraud cases (serialization-preventable):</p>
    <ul className="bullet-list">
      <li>Kouza Brothers: $15.0M</li>
      <li>Hamaed/Fakhuri ring: $13.0M</li>
      <li>LifeCare / Makki: $9.8M</li>
      <li>Makki / New Millennium: $6.9M</li>
      <li>Nabil Fakih: $4.0M</li>
      <li>Kirtland / Western Wayne: $1.5M</li>
      <li>P & A Aftercare: ~$1.0M (estimated)</li>
      <li>Divine Pharmacy: Amount TBD (smaller scale)</li>
    </ul>
    <p className="body-text"><strong>Total confirmed pharmacy fraud: $51.2M+ across 8 cases</strong></p>
    <p className="body-text">If serialized reimbursement verification had been operational during these schemes, the evidence suggests:</p>
    <ul className="bullet-list">
      <li>$48.3M in confirmed fraud could have been blocked at pre-payment (7 cases)</li>
      <li>~$1.0M (estimated) would likely have been detected significantly earlier through pattern flags (1 case)</li>
      <li>Detection lag could have been reduced from 5.8 years average to near real-time</li>
    </ul>
    <div className="key-finding highlight">
      <strong>Key Finding:</strong> The documented cases indicate that serialized verification could have prevented the substantial majority of identified pharmacy fraud dollar value in Michigan at the point of claim submission, before any payment was authorized.
    </div>
    <div className="page-number">17</div>
  </section>
);

/* PDF Pages 16-18 (Summary Table) */
const SummaryTable = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">SUMMARY TABLE</h2>
    <table className="data-table compact">
      <thead>
        <tr><th>Case</th><th>Amount</th><th>Fraud Type</th><th>Payers</th><th>Duration</th><th>Prevention Step</th><th>Mechanism</th></tr>
      </thead>
      <tbody>
        <tr><td>Kouza Brothers</td><td>$15.0M</td><td>Phantom dispensing</td><td>Medicare, Medicaid, BCBS</td><td>9 years</td><td>Step 4</td><td>Pre-payment block — no dispensing event</td></tr>
        <tr><td>Hamaed/Fakhuri (5 pharmacies)</td><td>$13.0M</td><td>Phantom dispensing</td><td>Medicare, Medicaid, BCBS</td><td>Multi-year</td><td>Step 4</td><td>Pre-payment block — no dispensing event</td></tr>
        <tr><td>LifeCare / Makki</td><td>$9.8M</td><td>Phantom dispensing + fabricated invoices</td><td>Medicare, Medicaid, BCBS</td><td>6 years</td><td>Step 3</td><td>Pre-payment block — no receipt record</td></tr>
        <tr><td>Makki / New Millennium</td><td>$6.9M</td><td>Phantom dispensing</td><td>Medicare, Medicaid, Commercial</td><td>Multi-year</td><td>Step 4</td><td>Pre-payment block — no dispensing event</td></tr>
        <tr><td>Nabil Fakih</td><td>$4.0M</td><td>Phantom dispensing (no inventory)</td><td>Medicare</td><td>6 years</td><td>Step 3</td><td>Pre-payment block — no receipt record</td></tr>
        <tr><td>Kirtland / Western Wayne</td><td>$1.5M</td><td>Phantom dispensing</td><td>Medicare, Medicaid</td><td>Multi-year</td><td>Step 4</td><td>Pre-payment block — no dispensing event</td></tr>
        <tr><td>P & A Aftercare / Detroit Hoover</td><td>~$1.0M</td><td>Controlled substance diversion</td><td>Medicare, Medicaid</td><td>Multi-year</td><td>Step 4</td><td>Real-time flag — pattern anomalies</td></tr>
        <tr><td>Divine Pharmacy / Onimo</td><td>TBD</td><td>Unauthorized practice</td><td>Medicaid</td><td>1–2 years</td><td>Step 5</td><td>Pre-payment block — suspended license</td></tr>
        <tr className="non-pharma-row"><td>Villa Nursing Homes</td><td>$4.5M</td><td>Services fraud (nursing care)</td><td>Medicare, Medicaid</td><td>Multi-year</td><td>N/A</td><td>Outside serialization scope</td></tr>
        <tr className="non-pharma-row"><td>Oxford Recovery / Peterson</td><td>TBD</td><td>Services fraud (behavioral health)</td><td>Medicaid</td><td>Multi-year</td><td>N/A</td><td>Outside serialization scope</td></tr>
        <tr className="non-pharma-row"><td>Dr. Carthron</td><td>$11K</td><td>Phantom telephone visits</td><td>Medicaid</td><td>4 months</td><td>N/A</td><td>Outside serialization scope</td></tr>
        <tr className="non-pharma-row"><td>Daleena Miller</td><td>$8.6K</td><td>CLS false documentation</td><td>Medicaid</td><td>Unknown</td><td>N/A</td><td>Outside serialization scope</td></tr>
      </tbody>
    </table>
    <p className="body-text" style={{ marginTop: '1rem' }}>
      <strong>Totals:</strong> Pharmacy fraud (serialization-addressable): 8 cases, $51.2M+ confirmed and estimated. Non-pharmacy fraud: 4 cases, $4.5M+ confirmed (additional cases have amounts TBD). Note: Dollar totals mix confirmed judgments/settlements with estimated exposure where noted.
    </p>
    <div className="page-number">18</div>
  </section>
);

/* PDF Page 18-19 (Implications for Michigan Pilot) */
const ImplicationsForPilot = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">IMPLICATIONS FOR THE MICHIGAN PILOT</h2>

    <h3 className="subsection-title">1. Drug Category Prioritization Based on Fraud Evidence</h3>
    <p className="body-text">The case evidence points to three clear priority categories for the Michigan serialization pilot:</p>

    <p className="body-text"><strong>Tier 1: High-Reimbursement Non-Controlled Pharmaceuticals</strong> — Antipsychotics, inhalers, blood thinners — the drug categories targeted in the Kouza Brothers ($15M), Hamaed/Fakhuri ring ($13M), LifeCare/Makki ($9.8M), and Fakih ($4M) cases. These drugs offer the highest reimbursement-to-scrutiny ratio: expensive enough to generate substantial fraud revenue, but not subject to the additional prescribing controls applied to controlled substances. Combined fraud exposure in this category: $41.8M+ across four cases.</p>

    <p className="body-text"><strong>Tier 2: Controlled Substances (Opioids, Benzodiazepines)</strong> — Oxycodone, Percocet, Norco targeted in the P & A Aftercare/Detroit Hoover diversion ring. Michigan's opioid crisis history creates heightened urgency for controlled substance chain-of-custody verification. Serialization adds a physical verification layer on top of PDMP monitoring, closing the gap between prescribing surveillance and dispensing verification.</p>

    <p className="body-text"><strong>Tier 3: Specialty Pharmaceuticals</strong> — HIV medications, hepatitis treatments, and other high-cost specialty drugs have been targeted in related national cases and represent the highest per-unit reimbursement rates. The national fraud pattern shows increasing targeting of specialty drugs as payers tighten controls on more common medications.</p>

    <h3 className="subsection-title">2. The Verification Gap in Michigan Pharmacy Claims</h3>
    <p className="body-text">
      The case evidence confirms a stark pattern: <strong>the documented major pharmacy fraud cases in Michigan exploited the same vulnerability.</strong> Claims were paid based on billing data alone. No payer — Medicare, Medicaid, or Blue Cross Blue Shield — verified that the physical drug was dispensed before authorizing payment.
    </p>
    <p className="body-text">
      This is not a technology failure unique to Michigan. It is a structural omission in the national pharmacy claims architecture. But Michigan's concentration of pharmacy fraud cases — $51.2M+ across 8 cases in one metropolitan area — demonstrates that the state's pharmacy claims environment is actively being exploited.
    </p>
    <p className="body-text">
      The 90-day pilot would generate the first empirical data on the verification gap in Michigan pharmacy claims. Specifically:
    </p>
    <ul className="bullet-list">
      <li>What percentage of Michigan Medicaid pharmacy claims can be matched to DSCSA serialization events?</li>
      <li>What percentage of claims have missing, inconsistent, or duplicate serialization data?</li>
      <li>What is the dollar value of claims that fail serialization verification?</li>
      <li>What fraud patterns emerge when serialization data is cross-referenced with claims data in real time?</li>
    </ul>
    <div className="page-number">19</div>
  </section>
);

/* PDF Page 19 continued (Strike Force + Sources) */
const StrikeForceAndSources = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h3 className="subsection-title">3. The Strike Force Presence: Risk and Opportunity</h3>
    <p className="body-text">The Eastern District of Michigan's permanent Health Care Fraud Strike Force creates a dual dynamic:</p>

    <p className="body-text"><strong>The Risk:</strong> Federal investigators are deeply embedded in Michigan's healthcare landscape. The state has been a focal point of national fraud enforcement for over a decade. Every major national takedown includes Michigan cases. This means that CMS and DOJ have extensive data on Michigan's fraud profile — and they know the state has a pharmacy fraud problem.</p>

    <p className="body-text">
      In the post-Minnesota-deferral environment, where CMS has demonstrated willingness to withhold federal matching funds from states that fail to address program integrity, Michigan's documented fraud pattern creates deferral risk. Michigan receives approximately $16.6 billion in federal Medicaid matching funds annually at a ~65.58% FMAP. A Minnesota-scale deferral ($259.5M) applied proportionally to Michigan would represent significant budget disruption.
    </p>

    <p className="body-text"><strong>The Opportunity:</strong> The same enforcement infrastructure that creates risk also creates a ready-made partnership for the serialization pilot. Michigan's Strike Force prosecutors and HHS-OIG investigators already understand the pharmacy fraud problem. They have prosecuted the cases that prove the verification gap. They are natural allies for a pilot that provides the prevention tool they've been lacking.</p>

    <p className="body-text">
      Implementing serialization-based verification gives Michigan a powerful demonstration of proactive program integrity — evidence it can present to CMS showing that the state is not merely responding to fraud after the fact, but working to prevent it before claims are paid. In the current enforcement environment, this distinction matters.
    </p>

    <h2 className="main-section-title" style={{ marginTop: '2rem' }}>SOURCES AND VERIFICATION</h2>
    <table className="data-table compact">
      <thead><tr><th>Case</th><th>Source</th><th>Status</th></tr></thead>
      <tbody>
        <tr><td>Kouza Brothers ($15M)</td><td>DOJ OPA press release, Nov 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Hamaed/Fakhuri ring ($13M)</td><td>DOJ OPA press release, Feb 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>LifeCare / Makki ($9.8M)</td><td>DOJ USAO-EDMI, Jan 2022</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Makki / New Millennium ($6.9M)</td><td>DOJ USAO-EDMI, Jun 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Nabil Fakih ($4M)</td><td>DOJ OPA press release, Nov 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Kirtland / Western Wayne ($1.5M)</td><td>DOJ USAO-EDMI civil resolution, Jun 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>P & A Aftercare / Detroit Hoover</td><td>DOJ USAO-EDMI / WNEM, Jun 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Divine Pharmacy / Onimo</td><td>MI AG press release, May 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Villa Nursing Homes ($4.5M)</td><td>DOJ USAO-EDMI civil resolution, Jun 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Oxford Recovery / Peterson</td><td>MI AG press release, Jan 2026</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Dr. Carthron ($11K)</td><td>MI AG press release, Aug 2025</td><td className="verified">VERIFIED</td></tr>
        <tr><td>Daleena Miller ($8.6K)</td><td>MLive, Jul 2025</td><td className="verified">VERIFIED</td></tr>
      </tbody>
    </table>
    <p className="body-text" style={{ marginTop: '1rem', fontStyle: 'italic' }}>
      All cases cited to DOJ, HHS-OIG, or Michigan AG primary sources. Verified against primary sources except where explicitly labeled estimated or approximate. No unsourced cases included.
    </p>
    <div className="page-number">20</div>
  </section>
);

/* Closing Page */
const Closing = () => (
  <section className="page-section closing-section page-break-before">
    <div className="section-header-bar">
      <span className="header-brand">MENDRX: SERIALIZED REIMBURSEMENT INFRASTRUCTURE</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <div className="contact-box">
      <p className="contact-header">For questions or to schedule a pilot discussion, contact:</p>
      <div className="contact-info">
        <div className="contact-person">
          <strong>Steve Madsen</strong>, Co-Founder
          <span>Steve@MendRx.Ai</span>
        </div>
        <div className="contact-person">
          <strong>Tony Madsen</strong>, Co-Founder
          <span>Tony@MendRx.Ai</span>
        </div>
      </div>
    </div>
    <div className="page-number">21</div>
  </section>
);

export default function FWACaseAnalysis() {
  const reportRef = useRef(null);
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);

  const exportToPDF = async () => {
    setIsExporting(true);
    const opt = {
      margin: [0.5, 0.6, 0.7, 0.6],
      filename: 'Michigan_Healthcare_Fraud_Case_Analysis.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      pagebreak: { mode: 'css', before: '.page-break-before' }
    };
    try {
      await html2pdf().set(opt).from(reportRef.current).save();
    } catch (error) {
      console.error('PDF export failed:', error);
    }
    setIsExporting(false);
  };

  return (
    <div className="report-container document-style">
      <div className="report-nav-bar">
        <motion.button
          className="back-button"
          onClick={() => navigate('/')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft size={18} />
          All Reports
        </motion.button>
        <motion.button
          className="export-button"
          onClick={exportToPDF}
          disabled={isExporting}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="export-pdf-btn"
        >
          <Download size={20} />
          {isExporting ? 'Generating PDF...' : 'Export to PDF'}
        </motion.button>
      </div>

      <div className="report-wrapper document" ref={reportRef}>
        <CoverPage />
        <main className="report-main">
          <ExecSummaryAndCase1Header />
          <Case1Detail />
          <Case1PreventionAndCase2Start />
          <Case2WhyAndPrevention />
          <Case3WhatHappened />
          <Case3PreventionAndCase4 />
          <Case4PreventionAndCase5Start />
          <Case5PreventionAndCase6 />
          <Case6PreventionAndCase7Start />
          <Case7WhyAndPrevention />
          <Case8 />
          <Case9 />
          <Case10 />
          <Cases11And12 />
          <PatternAnalysis1 />
          <PatternAnalysis2AndSummaryStart />
          <SummaryTable />
          <ImplicationsForPilot />
          <StrikeForceAndSources />
          <Closing />
        </main>
      </div>
    </div>
  );
}
